import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { OrderDirections } from '@robocorp/robocloud-search-iternal-sdk';
import { Button, Column, Menu, SortDirection } from '@robocorp/components';
import { IconChevronDown, IconSortAscending, IconSortDescending } from '@robocorp/icons/iconic';

type ModifiedColumn = Column & { menuTitle?: string };

export interface SettingsSortMenuProps {
  columns: ModifiedColumn[];
  sort: [string, SortDirection] | null;
  onSort: (sort: [string, SortDirection] | null) => void;
}

const getNextDirection = (direction?: SortDirection) => {
  if (!direction) return 'desc';
  if (direction === 'desc') return 'asc';
  return undefined;
};

export const useSortFilter = ({ columns, sort, onSort }: SettingsSortMenuProps) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sortableColumns = useMemo(() => columns.filter((column) => column.sortable), []);
  const currentSortedColumn = useMemo(
    () => (sort?.[0] ? sortableColumns.find((column) => column.id === sort?.[0]) : undefined),
    [sortableColumns, sort],
  );

  const onSortDirectionChange = useCallback(
    (id: string) => {
      const nextSortDirection = getNextDirection(id === sort?.[0] ? sort?.[1] : undefined);
      return onSort?.(nextSortDirection ? [id, nextSortDirection] : null);
    },
    [sort, onSort],
  );

  const onSortOrderChange = useCallback(
    (sortBy: string | undefined, sortOrder: SortDirection) => {
      if (sortBy !== undefined) {
        return onSort([sortBy, sortOrder]);
      }
      return null;
    },
    [onSort],
  );

  return { sortableColumns, currentSortedColumn, onSortDirectionChange, onSortOrderChange };
};

export const FilterSortMenu: FC<SettingsSortMenuProps> = ({ columns, sort, onSort }) => {
  const { t } = useTranslation();

  const { currentSortedColumn, sortableColumns, onSortDirectionChange, onSortOrderChange } =
    useSortFilter({
      columns,
      sort,
      onSort,
    });

  const sortOrder = sort?.[1];

  if (sortableColumns.length === 0) return null;

  return (
    <Menu
      size="small"
      trigger={
        <Button
          flex="1"
          variant="input"
          icon={sortOrder === OrderDirections.ASC ? IconSortAscending : IconSortDescending}
          iconAfter={IconChevronDown}
        >
          {currentSortedColumn
            ? (currentSortedColumn.menuTitle ?? currentSortedColumn.title)
            : t(`filter.sort.by`)}
        </Button>
      }
      matchWidth
    >
      <Menu.Title>{t('filter.sort.by')}</Menu.Title>
      {sortableColumns.map(({ id, menuTitle, title }) => (
        <Menu.Radio key={id} checked={id === sort?.[0]} onClick={() => onSortDirectionChange(id)}>
          {menuTitle ?? title}
        </Menu.Radio>
      ))}
      <Menu.Title>{t('filter.sort.order')}</Menu.Title>
      <Menu.Radio
        disabled={!currentSortedColumn}
        checked={sortOrder === OrderDirections.DESC}
        onClick={() => onSortOrderChange(sort?.[0], OrderDirections.DESC)}
      >
        {t('filter.sort.desc')}
      </Menu.Radio>
      <Menu.Radio
        disabled={!currentSortedColumn}
        checked={sortOrder === OrderDirections.ASC}
        onClick={() => onSortOrderChange(sort?.[0], OrderDirections.ASC)}
      >
        {t('filter.sort.asc')}
      </Menu.Radio>
    </Menu>
  );
};
